'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { pillBtnBase } from '@/components/roostr'
import { ROOMS_MAX } from '@/lib/constants'

// Shared range controls — the dual-thumb slider and the tap-to-range rooms
// stepper. Extracted verbatim from filter-modal.tsx (2026-08-02) so the
// MobileSearchSheet can render the SAME controls the desktop filters use
// instead of its old coarse "under 25k" / "2 rooms" preset chips. Same
// precedent as use-feature-groups.ts: one implementation, two surfaces.
// FilterModal now imports from here.

// Price bounds per transaction type — filters.jsx:8 (matches previous modal:
// rent 0–80k step 1k, sale 0–20M step 500k).
export const SALE_PRICE_MAX = 20_000_000
export const SALE_PRICE_STEP = 500_000

// The common Prague apartment-sale band, in absolute Kč — anchored to real
// prices (not a fraction of the max) so the slider's "sweet spot" stays put if
// the max ceiling ever changes.
const SALE_BAND_LO = 2_000_000
const SALE_BAND_HI = 12_000_000

// filters.jsx:34-37 — compact CZK amounts for the range readouts.
export const fmtK = (v: number, sale: boolean): string => {
  if (sale) return v >= 1_000_000 ? (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1) + 'M' : Math.round(v / 1000) + 'k'
  return v >= 1000 ? Math.round(v / 1000) + 'k' : String(v)
}

// Track position (0–1) ↔ value mapping for non-linear sliders.
export interface RangeScale {
  toSlider: (value: number, min: number, max: number) => number
  toValue: (position: number, min: number, max: number) => number
}

export const linearScale: RangeScale = {
  toSlider: (v, min, max) => (v - min) / (max - min),
  toValue: (t, min, max) => min + t * (max - min),
}

// Piecewise-linear sale scale: the popular SALE_BAND_LO–SALE_BAND_HI market
// band (2M–12M) gets 50% of the track, 0–2M gets 15%, and 12M–max gets 35% —
// so dragging around mid-track lands in the typical-apartment millions, not at
// the top of the range. Band edges are absolute (clamped to the domain).
export const saleScale: RangeScale = {
  toSlider: (value, min, max) => {
    const lo = Math.min(SALE_BAND_LO, max)
    const hi = Math.min(SALE_BAND_HI, max)
    if (value <= lo) return lo > min ? ((value - min) / (lo - min)) * 0.15 : 0
    if (value <= hi) return 0.15 + ((value - lo) / (hi - lo)) * 0.5
    return hi < max ? 0.65 + ((value - hi) / (max - hi)) * 0.35 : 1
  },
  toValue: (position, min, max) => {
    const lo = Math.min(SALE_BAND_LO, max)
    const hi = Math.min(SALE_BAND_HI, max)
    if (position <= 0.15) return min + (position / 0.15) * (lo - min)
    if (position <= 0.65) return lo + ((position - 0.15) / 0.5) * (hi - lo)
    return hi + ((position - 0.65) / 0.35) * (max - hi)
  },
}

// Typed-entry parsers: compact CZK ('25k', '7.5M', '7,5m') and plain area
// numbers ('80', '80 m2'). Return null for input we can't read — the field
// reverts instead of guessing.
export const parsePriceInput = (raw: string): number | null => {
  const s = raw.toLowerCase().replace(/kč|czk/g, '').replace(/\s/g, '').replace(',', '.')
  const m = s.match(/^(\d+(?:\.\d+)?)([km]?)$/)
  if (!m) return null
  const n = parseFloat(m[1])
  if (Number.isNaN(n)) return null
  return m[2] === 'k' ? n * 1000 : m[2] === 'm' ? n * 1_000_000 : n
}

export const parseAreaInput = (raw: string): number | null => {
  const s = raw.toLowerCase().replace(/m²|m2/g, '').replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(?:\.\d+)?$/.test(s)) return null
  const n = parseFloat(s)
  return Number.isNaN(n) ? null : n
}

// Dual-thumb range slider (pointer-based) — filters.jsx:39-94 ported verbatim
// (absolute-positioned thumbs on a 4px track, mono readout above), extended
// with typed MIN/MAX entry, full keyboard support (arrows, PageUp/Down,
// Home/End) and an optional non-linear scale. Aria values always reflect
// real values, never track positions.
export interface DualRangeProps {
  min: number
  max: number
  step: number
  value: [number, number]
  onChange: (v: [number, number]) => void
  format: (v: number) => string
  /** Position↔value mapping for the track (default linear). */
  scale?: RangeScale
  /** Compact editable text for the typed inputs (default String). */
  inputFormat?: (v: number) => string
  /** Parse typed input back to a number; null = invalid, revert. */
  inputParse?: (raw: string) => number | null
}

const defaultInputParse = (raw: string): number | null => {
  const s = raw.replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(?:\.\d+)?$/.test(s)) return null
  const n = parseFloat(s)
  return Number.isNaN(n) ? null : n
}

export function DualRange({
  min,
  max,
  step,
  value,
  onChange,
  format,
  scale = linearScale,
  inputFormat = String,
  inputParse = defaultInputParse,
}: DualRangeProps) {
  const { t } = useI18n()
  const [lo, hi] = value
  const trackRef = React.useRef<HTMLDivElement>(null)
  const valueRef = React.useRef<[number, number]>(value)
  valueRef.current = value

  // Cleanup for the active drag's window listeners, kept in a ref so unmount
  // (e.g. Escape closing the modal mid-drag) detaches them — otherwise they
  // keep firing and mutate the store after the modal is gone.
  const dragCleanupRef = React.useRef<(() => void) | null>(null)
  React.useEffect(() => () => { dragCleanupRef.current?.() }, [])

  const pct = (v: number) => Math.min(1, Math.max(0, scale.toSlider(v, min, max))) * 100
  const snap = (v: number) => Math.min(max, Math.max(min, Math.round(v / step) * step))
  const fromX = (clientX: number): number | null => {
    const el = trackRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return snap(scale.toValue(frac, min, max))
  }
  const startDrag = (which: 'lo' | 'hi') => (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCleanupRef.current?.()
    const move = (ev: PointerEvent) => {
      const v = fromX(ev.clientX)
      if (v == null) return // track detached — ignore stray moves
      const [curLo, curHi] = valueRef.current
      if (which === 'lo') onChange([Math.min(v, curHi), curHi])
      else onChange([curLo, Math.max(v, curLo)])
    }
    const up = () => {
      dragCleanupRef.current?.()
    }
    dragCleanupRef.current = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      dragCleanupRef.current = null
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  const onTrackDown = (e: React.PointerEvent) => {
    const v = fromX(e.clientX)
    if (v == null) return
    const [curLo, curHi] = valueRef.current
    if (Math.abs(v - curLo) <= Math.abs(v - curHi)) onChange([Math.min(v, curHi), curHi])
    else onChange([curLo, Math.max(v, curLo)])
  }
  const nudge = (which: 'lo' | 'hi', delta: number) => {
    const [curLo, curHi] = valueRef.current
    if (which === 'lo') onChange([Math.min(Math.max(min, curLo + delta), curHi), curHi])
    else onChange([curLo, Math.max(Math.min(max, curHi + delta), curLo)])
  }
  // ARIA slider pattern: arrows step, PageUp/Down big-step (10×), Home/End
  // jump to the extreme allowed by the other thumb.
  const onThumbKeyDown = (which: 'lo' | 'hi') => (e: React.KeyboardEvent) => {
    const [curLo, curHi] = valueRef.current
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault()
      nudge(which, -step)
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault()
      nudge(which, step)
    } else if (e.key === 'PageDown') {
      e.preventDefault()
      nudge(which, -step * 10)
    } else if (e.key === 'PageUp') {
      e.preventDefault()
      nudge(which, step * 10)
    } else if (e.key === 'Home') {
      e.preventDefault()
      if (which === 'lo') onChange([min, curHi])
      else onChange([curLo, curLo])
    } else if (e.key === 'End') {
      e.preventDefault()
      if (which === 'lo') onChange([curHi, curHi])
      else onChange([curLo, max])
    }
  }

  // ----- Typed MIN/MAX entry — commit on Enter/blur, clamp + snap to step;
  // extremes flow through onChange so the parent's null-at-extremes ("Any")
  // semantics apply exactly as they do for drags.
  const [loText, setLoText] = React.useState(() => inputFormat(lo))
  const [hiText, setHiText] = React.useState(() => inputFormat(hi))
  const [editingLo, setEditingLo] = React.useState(false)
  const [editingHi, setEditingHi] = React.useState(false)
  const inputFormatRef = React.useRef(inputFormat)
  inputFormatRef.current = inputFormat

  React.useEffect(() => {
    if (!editingLo) setLoText(inputFormatRef.current(lo))
  }, [lo, editingLo])
  React.useEffect(() => {
    if (!editingHi) setHiText(inputFormatRef.current(hi))
  }, [hi, editingHi])

  const commitInput = (which: 'lo' | 'hi', raw: string) => {
    const [curLo, curHi] = valueRef.current
    const parsed = inputParse(raw)
    if (parsed == null) {
      // Unreadable input — revert to the current value.
      if (which === 'lo') setLoText(inputFormatRef.current(curLo))
      else setHiText(inputFormatRef.current(curHi))
      return
    }
    const v = snap(parsed)
    if (which === 'lo') {
      const next = Math.min(v, curHi)
      setLoText(inputFormatRef.current(next))
      onChange([next, curHi])
    } else {
      const next = Math.max(v, curLo)
      setHiText(inputFormatRef.current(next))
      onChange([curLo, next])
    }
  }

  // Escape reverts instead of committing: the flag makes the blur that
  // follows skip its commit (the DOM still holds the abandoned text there).
  const revertOnBlurRef = React.useRef(false)
  const onInputKeyDown = (which: 'lo' | 'hi') => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur() // blur handler commits
    } else if (e.key === 'Escape') {
      // Revert and release focus without closing the whole modal.
      e.stopPropagation()
      revertOnBlurRef.current = true
      e.currentTarget.blur()
    }
  }

  const rangeInput = (which: 'lo' | 'hi') => (
    <label className="flex items-center gap-[5px]">
      <span className="text-[10px] font-bold tracking-[0.5px] text-ink-muted">
        {(which === 'lo'
          ? t('filterModal.minShort', 'Min')
          : t('filterModal.maxShort', 'Max')
        ).toUpperCase()}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={which === 'lo' ? loText : hiText}
        aria-label={which === 'lo' ? t('filterModal.minValue', 'Minimum') : t('filterModal.maxValue', 'Maximum')}
        onChange={(e) => (which === 'lo' ? setLoText(e.target.value) : setHiText(e.target.value))}
        onFocus={(e) => {
          if (which === 'lo') setEditingLo(true)
          else setEditingHi(true)
          e.currentTarget.select()
        }}
        onBlur={(e) => {
          if (revertOnBlurRef.current) {
            revertOnBlurRef.current = false
            const [curLo, curHi] = valueRef.current
            if (which === 'lo') setLoText(inputFormatRef.current(curLo))
            else setHiText(inputFormatRef.current(curHi))
          } else {
            commitInput(which, e.currentTarget.value)
          }
          if (which === 'lo') setEditingLo(false)
          else setEditingHi(false)
        }}
        onKeyDown={onInputKeyDown(which)}
        // Wider on mobile than the desktop 72px: the "Any" placeholder is the
        // widest thing these fields ever hold, and Czech renders it
        // "Libovolné", which clipped to "Libovol" at 78px.
        className="w-[104px] rounded-full border border-outline bg-surface-low px-[10px] py-[7px] text-right font-mono text-[13px] font-semibold text-ink outline-none transition-colors focus:border-primary sm:w-[86px] sm:py-[4px] sm:text-[12px]"
      />
    </label>
  )
  // Per the ARIA multi-thumb pattern each thumb advertises the range it can
  // actually reach: the lo thumb is capped by hi and vice versa.
  const thumb = (v: number, which: 'lo' | 'hi') => (
    <button
      type="button"
      role="slider"
      aria-valuemin={which === 'lo' ? min : lo}
      aria-valuemax={which === 'lo' ? hi : max}
      aria-valuenow={v}
      aria-valuetext={format(v)}
      aria-label={which === 'lo' ? t('filterModal.minValue', 'Minimum') : t('filterModal.maxValue', 'Maximum')}
      onPointerDown={startDrag(which)}
      onKeyDown={onThumbKeyDown(which)}
      title={format(v)}
      // The 20px dot is the VISUAL; the ::before pad grows the hit area to
      // 44x44 (Apple HIG minimum) without moving a pixel on screen. Two thumbs
      // sit on one track and this is the primary price/area control, so a
      // 20px target was the hardest thing to grab in the app.
      className="absolute h-5 w-5 cursor-grab touch-none rounded-full border-[2.5px] border-primary bg-surface shadow-e1 before:absolute before:-inset-3 before:rounded-full before:content-['']"
      style={{ left: `calc(${pct(v)}% - 10px)`, top: -8 }}
    />
  )
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="font-mono text-[13px] font-bold text-ink">
          {format(lo)} <span className="font-normal text-ink-muted">—</span> {format(hi)}
        </div>
        <div className="flex items-center gap-2">
          {rangeInput('lo')}
          {rangeInput('hi')}
        </div>
      </div>
      <div
        ref={trackRef}
        onPointerDown={onTrackDown}
        className="relative mx-[10px] mb-[6px] h-1 cursor-pointer rounded-[2px] bg-surface-high"
      >
        <span
          className="absolute bottom-0 top-0 rounded-[2px] bg-primary"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        {thumb(lo, 'lo')}
        {thumb(hi, 'hi')}
      </div>
    </div>
  )
}

// Rooms as tappable steps — filters.jsx:96-138. First tap = exactly N, tapping
// the same N clears, a second different tap makes a range, a tap during a
// range restarts with exactly N. Writes the production rooms_min/rooms_max
// fields ("6" means 6+, stored as rooms_max undefined).
export interface RoomsRangeProps {
  lo: number | null
  hi: number | null
  onChange: (v: [number | null, number | null]) => void
}

export function RoomsRange({ lo, hi, onChange }: RoomsRangeProps) {
  const { t, uiLanguage } = useI18n()
  const pick = (n: number) => {
    if (lo == null || hi == null || lo !== hi) onChange([n, n])
    else if (n === lo) onChange([null, null])
    else onChange([Math.min(lo, n), Math.max(lo, n)])
  }
  const inRange = (n: number) => lo != null && n >= lo && n <= (hi == null ? ROOMS_MAX : hi)

  const roomsWord = (n: number): string => {
    if (uiLanguage === 'cs') {
      if (n === 1) return t('filterModal.roomOne', 'room')
      if (n >= 2 && n <= 4) return t('filterModal.roomFew', 'rooms')
      return t('filterModal.roomMany', 'rooms')
    }
    return n === 1 ? t('filterModal.roomOne', 'room') : t('filterModal.roomMany', 'rooms')
  }
  const roomsPhrase = (n: number) => `${n}${n === ROOMS_MAX ? '+' : ''} ${roomsWord(n)}`

  const caption =
    lo == null
      ? t('filterModal.roomsHintEmpty', 'Tap a number — tap a second one to make a range.')
      : lo === hi
        ? t('filterModal.roomsHintExact', 'Exactly {rooms} — tap another number for a range.').replace('{rooms}', roomsPhrase(lo))
        : t('filterModal.roomsHintRange', '{lo} to {rooms}.')
            .replace('{lo}', String(lo))
            .replace('{rooms}', roomsPhrase(hi ?? ROOMS_MAX))

  return (
    <div>
      <div className="flex gap-[6px]">
        <button
          type="button"
          onClick={() => onChange([null, null])}
          className={cn(
            pillBtnBase,
            'px-[14px] py-[9px] text-[13px]',
            lo == null ? 'bg-primary text-white' : 'bg-surface-low text-ink-secondary'
          )}
        >
          {t('search.any', 'Any')}
        </button>
        {Array.from({ length: ROOMS_MAX }).map((_, i) => {
          const n = i + 1
          const on = inRange(n)
          return (
            <button
              key={n}
              type="button"
              onClick={() => pick(n)}
              className={cn(
                pillBtnBase,
                'flex-1 justify-center px-0 py-[9px] text-[13px]',
                on ? 'bg-primary text-white' : 'bg-surface-low text-ink-secondary'
              )}
            >
              {n}
              {n === ROOMS_MAX ? '+' : ''}
            </button>
          )
        })}
      </div>
      <div className="mt-[7px] text-[11.5px] text-ink-muted">{caption}</div>
    </div>
  )
}
